/**
 * Drawer component with slide animation and accessibility
 */
import React, { useEffect, useRef } from 'react';
import { colors, spacing, typography, shadows, transitions, typographyFlat } from '../../styles/tokens';

type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  position?: DrawerPosition;
  width?: string;
  showCloseButton?: boolean;
}

const positionStyles: Record<DrawerPosition, React.CSSProperties> = {
  left: {
    top: 0,
    left: 0,
    bottom: 0,
    width: `calc(${spacing.lg} * 6)`,
  },
  right: {
    top: 0,
    right: 0,
    bottom: 0,
    width: `calc(${spacing.lg} * 6)`,
  },
  top: {
    top: 0,
    left: 0,
    right: 0,
    height: '50vh',
  },
  bottom: {
    bottom: 0,
    left: 0,
    right: 0,
    height: '50vh',
  },
};

const slideTransforms: Record<DrawerPosition, { closed: string; open: string }> = {
  left: { closed: 'translateX(-100%)', open: 'translateX(0)' },
  right: { closed: 'translateX(100%)', open: 'translateX(0)' },
  top: { closed: 'translateY(-100%)', open: 'translateY(0)' },
  bottom: { closed: 'translateY(100%)', open: 'translateY(0)' },
};

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = 'right',
  width,
  showCloseButton = true,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    // Focus close button on open
    closeButtonRef.current?.focus();

    // Handle Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isOpen ? `${colors.text}80` : 'transparent',
    zIndex: 1000,
    opacity: isOpen ? 1 : 0,
    transition: `opacity ${transitions.base}`,
    pointerEvents: isOpen ? 'auto' : 'none',
  };

  const drawerStyle: React.CSSProperties = {
    position: 'fixed',
    backgroundColor: colors.background,
    boxShadow: shadows.xl,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001,
    transform: isOpen ? slideTransforms[position].open : slideTransforms[position].closed,
    transition: `transform ${transitions.slow} ease-in-out`,
    ...positionStyles[position],
    ...(width && (position === 'left' || position === 'right') ? { width } : {}),
  };

  const headerStyle: React.CSSProperties = {
    padding: spacing.lg,
    borderBottom: `1px solid ${colors.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: typography.h2.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: typographyFlat.fontSize.xl,
    color: colors.textSecondary,
    cursor: 'pointer',
    padding: spacing.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${spacing.lg}`,
    height: `${spacing.lg}`,
  };

  const contentStyle: React.CSSProperties = {
    padding: spacing.lg,
    overflowY: 'auto',
    flex: 1,
  };

  return (
    <>
      <div
        style={overlayStyle}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <aside
        ref={drawerRef}
        style={drawerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        aria-hidden={!isOpen}
      >
        {(title || showCloseButton) && (
          <header style={headerStyle}>
            {title && (
              <h2 id="drawer-title" style={titleStyle}>
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                ref={closeButtonRef}
                style={closeButtonStyle}
                onClick={onClose}
                aria-label="Close drawer"
                type="button"
              >
                ×
              </button>
            )}
          </header>
        )}
        <div style={contentStyle}>{children}</div>
      </aside>
    </>
  );
};
